import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, RefreshCw, Trash2, UploadCloud, AlertTriangle, PlayCircle, Camera } from "lucide-react";
import MuxPlayer from "@mux/mux-player-react";
import { useIntroVideo } from "../lib/introVideo";
import { VideoRecorder } from "./VideoRecorder";

/**
 * IntroVideoUpload — Mux-powered upload + adaptive-streaming player.
 *
 * Direct-to-Mux flow: the file bytes leave the browser via a signed URL
 * from Mux (never proxied through our FastAPI backend). Our backend just
 * signs the URL and mirrors the resulting playback_id into Mongo.
 *
 * States (mutually exclusive):
 *   idle       — no video yet, show drop zone
 *   uploading  — file is streaming to Mux, show progress bar
 *   processing — Mux is encoding, poll until ready
 *   ready      — render Mux Player
 *   errored    — show error + retry
 */
const ACCEPT = "video/mp4,video/webm,video/quicktime";
const MAX_MB = 150;
const MAX_MIN = 5;

const humanBytes = (n) => {
  if (!n) return "";
  const mb = n / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`;
};

export const IntroVideoUpload = ({ campaignId = null }) => {
  const { current, uploading, progress, error, upload, remove, forceSync } = useIntroVideo({ campaignId });
  const inputRef = useRef(null);
  const [recording, setRecording] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const state =
    uploading ? "uploading"
    : current?.status === "ready" ? "ready"
    : current?.status === "errored" ? "errored"
    : current?.status === "processing" || current?.status === "waiting_for_upload" ? "processing"
    : "idle";

  return (
    <div data-testid="intro-video-upload" className="mt-5">
      <AnimatePresence mode="wait">
        {state === "ready" && (
          <motion.div
            key="ready"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            data-testid="intro-video-ready"
          >
            <div className="overflow-hidden rounded-xl border border-border bg-ink">
              <MuxPlayer
                playbackId={current.playback_id}
                streamType="on-demand"
                poster={current.poster_url}
                thumbnailTime={1}
                metadata={{
                  video_id: current.asset_id,
                  video_title: current.filename || "Campaign intro",
                }}
                accentColor="#FD6106"
                className="aspect-video w-full"
                data-testid="intro-video-player"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-sand px-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-serif text-3xl text-ink">
                  {current.filename || "Intro video"}
                </p>
                <p className="text-[13px] text-olive">
                  {current.content_type} · {humanBytes(current.size)} · streaming via Mux
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(current.id)}
                data-testid="intro-video-remove"
                className="inline-flex items-center gap-1.5 rounded-full border border-scarlet/30 bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-scarlet transition-colors hover:bg-scarlet/8"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          </motion.div>
        )}

        {state === "processing" && (
          <motion.div
            key="processing"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-start gap-4 rounded-xl border-2 border-tangerine/30 bg-tangerine/[0.04] p-6"
            data-testid="intro-video-processing"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-tangerine/15 text-tangerine">
              <PlayCircle className="h-6 w-6" strokeWidth={1.9} />
            </span>
            <div>
              <p className="font-serif text-3xl text-ink">Mux is preparing your video.</p>
              <p className="mt-2 max-w-md text-[13px] text-olive">
                We're encoding for smooth playback on every connection — typically 15–60 seconds. You can close this tab and come back.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => forceSync(current.id)}
                  data-testid="intro-video-refresh"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
                >
                  <RefreshCw className="h-3 w-3" /> Check now
                </button>
                <button
                  type="button"
                  onClick={() => remove(current.id)}
                  data-testid="intro-video-cancel"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-sand px-3.5 py-1.5 text-[13px] font-semibold text-olive"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {state === "uploading" && (
          <motion.div
            key="uploading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-ink/25 bg-sand p-8"
            data-testid="intro-video-uploading"
          >
            <Loader2 className="h-8 w-8 animate-spin text-ink" strokeWidth={1.5} />
            <p className="font-serif text-3xl text-ink">Sending to Mux…</p>
            <div className="w-full max-w-sm">
              <div className="h-2 w-full overflow-hidden rounded-full bg-border">
                <div
                  className="h-full bg-ink transition-all"
                  style={{ width: `${progress}%` }}
                  data-testid="intro-video-progress-bar"
                />
              </div>
              <p className="mt-2 text-center text-[13px] text-olive" data-testid="intro-video-progress">
                {progress}%
              </p>
            </div>
          </motion.div>
        )}

        {state === "errored" && (
          <motion.div
            key="errored"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border-2 border-scarlet/40 bg-scarlet/6 p-6"
            data-testid="intro-video-errored"
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-scarlet/15 text-scarlet">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <div>
                <p className="font-serif text-3xl text-ink">That video couldn't be processed.</p>
                <p className="mt-1 max-w-md text-[13px] text-olive">
                  {current?.error || "The file may be corrupted or an unsupported format. Try re-exporting as MP4 (H.264) and uploading again."}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => remove(current.id)}
                    data-testid="intro-video-clear-error"
                    className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-[13px] font-semibold text-sand hover:bg-forest"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {state === "idle" && !recording && (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-sand p-8 text-center transition-colors hover:border-ink/50"
          >
            <UploadCloud className="h-8 w-8 text-olive" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-3xl text-ink">
              Drop your video, or choose how to add one.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                data-testid="intro-video-browse"
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-bone px-4 py-2 text-base font-semibold text-ink transition-colors hover:border-ink"
              >
                <UploadCloud className="h-4 w-4" /> Upload a file
              </button>
              <span className="text-[13px] text-olive">or</span>
              <button
                type="button"
                onClick={() => setRecording(true)}
                data-testid="intro-video-record-open"
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                <Camera className="h-4 w-4" /> Record with camera
              </button>
            </div>
            <p className="mt-3 text-[13px] text-olive">
              MP4, WebM, or MOV. Up to {MAX_MIN} minutes or {MAX_MB} MB — Mux streams it to donors on any connection.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={onPick}
              className="hidden"
              data-testid="intro-video-input"
            />
          </motion.div>
        )}

        {state === "idle" && recording && (
          <motion.div
            key="recorder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <VideoRecorder
              onCapture={(file) => {
                setRecording(false);
                upload(file);
              }}
              onCancel={() => setRecording(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p role="alert" data-testid="intro-video-error" className="mt-3 text-[13px] text-scarlet">
          {error}
        </p>
      )}
    </div>
  );
};
